import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  useCreateBackendUserBotConnectionMutation,
  useDeleteAdminConnectionMutation,
  useGetBotRegistryQuery,
  useUpdateAdminConnectionMutation,
} from "features/adminUsers/adminUsers";
import { AdminVendorConnection } from "types/interfaces/adminUsers";
import AppDialog from "components/common/AppDialog";
import {
  AutocompleteScrollListbox,
  autocompleteScrollPaperProps,
} from "components/common/AutocompleteScrollListbox";
import BotsRecordTable, {
  type BotsColumn,
} from "components/botsManagement/BotsRecordTable";
import ConnectionFields from "components/botsManagement/ConnectionFields";
import ConnectionProblemFlag from "components/botsManagement/ConnectionProblemFlag";
import {
  ConnectionFieldMessages,
  ConnectionValues,
  EMPTY_CONNECTION_VALUES,
  connectionBody,
  connectionRulesFor,
  missingConnectionFields,
  serverConnectionErrors,
} from "components/botsManagement/connectionFields";
import useResponsive from "hooks/useResponsive";
import BotsTopBar from "components/botsManagement/BotsTopBar";

const CENTER_BODY_CELL_SX = { textAlign: "center" as const, verticalAlign: "middle" as const };

export type ConnectionsPanelProps = {
  /** The client switcher, first crumb of the header. */
  switcher?: ReactNode;
  userId: number;
  userLabel?: string;
  connections: AdminVendorConnection[];
  connectionsLoading?: boolean;
  connectionsError?: boolean;
  onRefreshConnections: () => Promise<unknown>;
  onBack?: () => void;
  canEdit: boolean;
};

export default function ConnectionsPanel({
  switcher,
  userId,
  userLabel,
  connections,
  connectionsLoading = false,
  connectionsError = false,
  onRefreshConnections,
  onBack,
  canEdit,
}: ConnectionsPanelProps) {
  const [createConnection, { isLoading: isCreating }] = useCreateBackendUserBotConnectionMutation();
  const [updateConnection, { isLoading: isSaving }] = useUpdateAdminConnectionMutation();
  const [deleteConnection, { isLoading: isDeleting }] = useDeleteAdminConnectionMutation();

  const { data: botRegistryForVendors } = useGetBotRegistryQuery({ isActive: true, pageSize: 500 });
  const registryVendors =
    botRegistryForVendors && !Array.isArray(botRegistryForVendors)
      ? botRegistryForVendors.vendors
      : undefined;

  const [editTarget, setEditTarget] = useState<AdminVendorConnection | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [vendor, setVendor] = useState("");
  const [values, setValues] = useState<ConnectionValues>(EMPTY_CONNECTION_VALUES);
  const [fieldErrors, setFieldErrors] = useState<ConnectionFieldMessages>({});
  const [vendorError, setVendorError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const rules = connectionRulesFor(registryVendors, vendor);

  // Only vendors that have a bot (CAL-6158); a login saved under an older
  // name keeps it while it is edited.
  const vendorSelectOptions = useMemo(() => {
    const s = new Set(Object.keys(registryVendors ?? {}));
    const kept = (editTarget?.vendor ?? "").trim();
    if (kept) s.add(kept);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [registryVendors, editTarget?.vendor]);

  const [rowDeleteTarget, setRowDeleteTarget] = useState<AdminVendorConnection | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);

  useEffect(() => {
    setSelected((prev) => prev.filter((id) => connections.some((c) => c.id === id)));
  }, [connections]);

  const idsOnPage = useMemo(() => connections.map((c) => c.id), [connections]);
  const allSelected = idsOnPage.length > 0 && idsOnPage.every((id) => selected.includes(id));
  const someSelected = idsOnPage.some((id) => selected.includes(id));

  const toggleAll = (checked: boolean) => {
    setSelected((prev) => {
      if (checked) return Array.from(new Set([...prev, ...idsOnPage]));
      const drop = new Set(idsOnPage);
      return prev.filter((id) => !drop.has(id));
    });
  };

  const toggleOne = (id: number, checked: boolean) => {
    setSelected((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const resetMessages = () => {
    setFieldErrors({});
    setVendorError(null);
    setFormError(null);
  };

  const openCreate = () => {
    setEditTarget(null);
    setVendor("");
    setValues(EMPTY_CONNECTION_VALUES);
    resetMessages();
    setEditOpen(true);
  };

  const openEdit = (row: AdminVendorConnection) => {
    setEditTarget(row);
    setVendor(row.vendor ?? "");
    setValues({
      host: row.host ?? "",
      client_code: row.client_code ?? "",
      username: row.username ?? "",
      // Backend returns a decrypted password for admin users
      // (AdminVendorConnectionSerializer.get_password) so we pre-fill it here
      // and let the user reveal it via the eye toggle.
      password: row.password ?? "",
    });
    resetMessages();
    // A saved login that breaks its vendor's rules shows where at once.
    setFieldErrors(row.problems ?? {});
    setEditOpen(true);
  };

  const save = async () => {
    resetMessages();
    if (!vendor.trim()) {
      setVendorError("Pick a vendor.");
      return;
    }
    const missing = missingConnectionFields(rules, values);
    if (Object.keys(missing).length) {
      setFieldErrors(missing);
      return;
    }
    // Only the fields this vendor shows: a hidden one keeps its stored value,
    // and the password goes back as shown (prefilled on edit).
    const body = { ...connectionBody(rules, values), vendor: vendor.trim() };
    try {
      if (editTarget) {
        await updateConnection({ connectionId: editTarget.id, body }).unwrap();
      } else {
        await createConnection({ userId, body }).unwrap();
      }
      await onRefreshConnections();
      setEditOpen(false);
    } catch (error) {
      const refused = serverConnectionErrors(error);
      setFieldErrors(refused.fields);
      setVendorError(refused.vendor);
      const atFields = Object.keys(refused.fields).length > 0 || refused.vendor;
      setFormError(
        refused.other ??
          (atFields
            ? null
            : editTarget
              ? "Failed to save connection."
              : "Failed to create connection."),
      );
    }
  };

  const confirmRowDelete = async () => {
    if (!rowDeleteTarget) return;
    try {
      await deleteConnection(rowDeleteTarget.id).unwrap();
      setRowDeleteTarget(null);
      await onRefreshConnections();
    } catch {
      /* keep dialog open so the user can see the error via UI alerts */
    }
  };

  const confirmBulkDelete = async () => {
    if (!selected.length) return;
    try {
      for (const cid of selected) {
        await deleteConnection(cid).unwrap();
      }
      setSelected([]);
      setBulkDeleteOpen(false);
      await onRefreshConnections();
    } catch {
      /* error: leave dialog open */
    }
  };

  const { isCardMode, is2K } = useResponsive();

  /** A stored host as is; an empty one says what the vendor's bots do then. */
  const hostLabel = (row: AdminVendorConnection) => {
    const host = row.host?.trim();
    if (host) return host;
    const use = connectionRulesFor(registryVendors, row.vendor ?? "").host.use;
    if (use === "unused") return "not used";
    if (use === "required") return "-";
    return `default ${row.vendor?.trim() || "vendor"} host`;
  };

  const connectionColumns: BotsColumn<AdminVendorConnection>[] = useMemo(
    () => [
      {
        key: "id",
        header: "No",
        width: 70,
        card: "hidden",
        // The connection's id, as every login picker shows it (CAL-6224).
        cell: (row) => `#${row.id}`,
      },
      {
        key: "login",
        header: "Login",
        width: is2K ? 360 : 200,
        card: "title",
        cell: (row) => {
          const label = row.username?.trim() ? `@${row.username.trim()}` : "-";
          return (
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <Typography variant="body2" noWrap title={label}>
                {label}
              </Typography>
              <ConnectionProblemFlag connection={row} />
            </Box>
          );
        },
        cardCell: (row) => (
          <Typography fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
            {`${row.username?.trim() ? `@${row.username.trim()}` : "-"} \u00b7 #${row.id}`}
            <ConnectionProblemFlag connection={row} />
          </Typography>
        ),
      },
      {
        key: "vendor",
        header: "Vendor",
        width: 140,
        cell: (row) => (
          <Typography variant="body2" noWrap title={row.vendor}>
            {row.vendor}
          </Typography>
        ),
      },
      {
        key: "host",
        header: "Host",
        cell: (row) => {
          const label = hostLabel(row);
          return (
            <Typography
              variant="body2"
              noWrap
              title={label}
              color={row.host?.trim() ? "text.primary" : "text.secondary"}
            >
              {label}
            </Typography>
          );
        },
        cardCell: (row) => (
          <Typography
            variant="body2"
            sx={{ overflowWrap: "anywhere" }}
            color={row.host?.trim() ? "text.primary" : "text.secondary"}
          >
            {hostLabel(row)}
          </Typography>
        ),
      },
      {
        key: "clientCode",
        header: "Client code",
        width: 150,
        cell: (row) => (
          <Typography variant="body2" noWrap title={row.client_code ?? ""}>
            {row.client_code?.trim() || "-"}
          </Typography>
        ),
      },
      {
        key: "actions",
        header: "Actions",
        width: 150,
        card: "actions",
        cell: (row) => (
          <Stack
            direction="row"
            spacing={0.5}
            justifyContent="center"
            alignItems="center"
          >
            <Button size="small" onClick={() => openEdit(row)} disabled={isSaving}>
              Edit
            </Button>
            <Tooltip title="Delete connection">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="Delete connection"
                  disabled={!canEdit}
                  onClick={() => setRowDeleteTarget(row)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [canEdit, isSaving, openEdit, is2K, registryVendors],
  );

  const headerActions = (
    <>
      <Button
        variant="contained"
        onClick={openCreate}
        disabled={!canEdit || isCreating}
        sx={{ height: 36, fontSize: 13, whiteSpace: "nowrap" }}
      >
        New Connection
      </Button>
      <Button
        color="error"
        variant="outlined"
        onClick={() => setBulkDeleteOpen(true)}
        disabled={!canEdit || !selected.length || isDeleting}
        sx={{ height: 36, fontSize: 13, whiteSpace: "nowrap" }}
      >
        {isCardMode ? "Delete" : "Delete selected"}
      </Button>
    </>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <BotsTopBar
        lead={switcher}
        crumbs={onBack ? [{ label: "Major jobs", onClick: onBack }] : []}
        title="Connections"
        titleText={userLabel ? `Connections of ${userLabel}` : "Connections"}
        actions={isCardMode ? undefined : headerActions}
        stacked={isCardMode}
      />
      {isCardMode && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1,
            px: 2,
            pb: 1.5,
            "& .MuiButton-root": { height: 40 },
          }}
        >
          {headerActions}
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          flex: 1,
          minHeight: 0,
          px: { xs: 2, md: 3 },
          py: 2,
        }}
      >
      {connectionsError && (
        <Alert severity="error">Failed to load connections for this user.</Alert>
      )}

      <BotsRecordTable<AdminVendorConnection>
        rows={connections}
        rowKey={(row) => row.id}
        fill
        loading={connectionsLoading}
        empty={
          <Typography color="text.secondary">
            No bot connections for this user yet.
          </Typography>
        }
        selection={{
          selectedIds: selected,
          onToggle: (id, checked) => toggleOne(Number(id), checked),
          onToggleAll: toggleAll,
          allLabel: "Select all connections",
          disabled: !canEdit,
        }}
        columns={connectionColumns}
      />
      </Box>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        title={<>{editTarget ? "Edit Connection" : "New Connection"}</>}
        actions={
          <>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => void save()}
              disabled={!canEdit || isSaving || isCreating}
            >
              {isSaving || isCreating ? "Saving..." : editTarget ? "Save" : "Create"}
            </Button>
          </>
        }
      >
        <Autocomplete
          ListboxComponent={AutocompleteScrollListbox}
          componentsProps={autocompleteScrollPaperProps}
          sx={{ mt: 1, mb: 1.5 }}
          options={vendorSelectOptions}
          value={vendor || null}
          onChange={(_e, picked) => {
            setVendor(picked ?? "");
            setVendorError(null);
            setFieldErrors({});
          }}
          ListboxProps={{ style: { maxHeight: 280 } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vendor"
              required
              error={Boolean(vendorError)}
              helperText={vendorError || "The vendor decides which fields its bots read."}
            />
          )}
        />
        {vendor.trim() && (
          <ConnectionFields
            rules={rules}
            vendor={vendor}
            values={values}
            errors={fieldErrors}
            onChange={(name, value) => {
              setValues((prev) => ({ ...prev, [name]: value }));
              setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
            }}
          />
        )}
        {formError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {formError}
          </Alert>
        )}
      </AppDialog>

      <AppDialog
        open={rowDeleteTarget != null}
        onClose={() => setRowDeleteTarget(null)}
        title="Delete connection?"
        actions={
          <>
            <Button onClick={() => setRowDeleteTarget(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              disabled={isDeleting || rowDeleteTarget == null}
              onClick={() => void confirmRowDelete()}
            >
              Delete
            </Button>
          </>
        }
      >
        <Typography variant="body2">
          {rowDeleteTarget ? (
            <>
              Delete connection{" "}
              <strong>
                {rowDeleteTarget.username?.trim() ? `@${rowDeleteTarget.username.trim()}` : "-"}
              </strong>{" "}
              ({rowDeleteTarget.vendor})? This cannot be undone.
            </>
          ) : null}
        </Typography>
      </AppDialog>

      <AppDialog
        open={bulkDeleteOpen}
        onClose={() => !isDeleting && setBulkDeleteOpen(false)}
        title="Delete selected connections?"
        actions={
          <>
            <Button onClick={() => setBulkDeleteOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={!selected.length || isDeleting}
              onClick={() => void confirmBulkDelete()}
            >
              Delete
            </Button>
          </>
        }
      >
        <Typography variant="body2">
          Delete {selected.length} selected connection{selected.length === 1 ? "" : "s"}? This cannot be
          undone.
        </Typography>
      </AppDialog>
    </Box>
  );
}
